'use client';

import { useState } from 'react';
import { hasSellerAccount } from '@/platform/marketplace/useMarketplaceSellLabel';
import { pauseSelling, type SellerCapabilities } from './api';

export interface SellerPauseSellingProps {
  caps: SellerCapabilities | null;
  onPaused: () => void;
}

export function SellerPauseSelling({ caps, onPaused }: SellerPauseSellingProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!caps || !hasSellerAccount(caps)) {
    return null;
  }

  async function submitPause() {
    setBusy(true);
    setError('');
    try {
      await pauseSelling();
      setConfirmOpen(false);
      onPaused();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not pause selling');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="seller-pause">
      <h2 className="vendor-console__section-title">Pause marketplace selling</h2>
      <p className="vendor-console__hint">
        Hide your store, archive active listings, and clear marketplace seller terms. Your Stripe Connect account
        stays linked, and verified storefront status is kept so you can resume without re-applying.
      </p>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {!confirmOpen ? (
        <p>
          <button type="button" className="btn btn-ghost" onClick={() => setConfirmOpen(true)}>
            Pause selling
          </button>
        </p>
      ) : (
        <div className="seller-pause__confirm">
          <p className="text-muted">
            Open paid orders must be fulfilled or refunded before pausing. Active listings will stop appearing on the
            marketplace.
          </p>
          <p>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void submitPause()}>
              {busy ? 'Pausing…' : 'Confirm pause selling'}
            </button>{' '}
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirmOpen(false)}>
              Cancel
            </button>
          </p>
        </div>
      )}
    </section>
  );
}
